import { FiAlertTriangle } from 'react-icons/fi';
import SelectTextField from '../../../molecules/SelectTextField';
import TextEdit from '../../../molecules/TextEdit';
import DateField from '../../../molecules/DateField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import { HearingOutcome, type SelectOption } from '../../../../types/LegalCase';
import type { ErrorField } from '../../../../types/ErrorField';
import useLegalCaseHearingForm, { residualErrors } from '../../../../hooks/useLegalCaseHearingForm';
import type { AddHearingValue } from '../../../../hooks/useLegalCaseHearings';

interface LegalCaseHearingFormProps {
    outcomeOptions: SelectOption[];
    errorFields: ErrorField[];
    isSaving: boolean;
    onSubmit: (value: AddHearingValue) => Promise<boolean>;
    onCancel: () => void;
}

// Só JSX + bindings: estado e regras do formulário vivem em useLegalCaseHearingForm.
const LegalCaseHearingForm = ({
    outcomeOptions,
    errorFields,
    isSaving,
    onSubmit,
    onCancel,
}: LegalCaseHearingFormProps) => {
    const form = useLegalCaseHearingForm({ onSubmit });

    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <DateField
                    label="Data da audiência"
                    value={form.scheduledAt}
                    onChange={form.setScheduledAt}
                    errorMessage={getErrorMessage(errorFields, 'scheduledAt')}
                />
                <DateField
                    label="Realizada em (opcional)"
                    value={form.heldAt}
                    onChange={form.setHeldAt}
                    errorMessage={getErrorMessage(errorFields, 'heldAt')}
                />
            </div>

            <SelectTextField
                label="Desfecho (opcional)"
                items={outcomeOptions}
                emptyLabel="— sem desfecho —"
                value={form.outcomeStatus}
                onChange={(e) => form.setOutcomeStatus(e.target.value as HearingOutcome | '')}
                errorMessage={getErrorMessage(errorFields, 'outcomeStatus')}
            />

            <TextEdit
                label="Detalhe do desfecho (opcional)"
                value={form.outcome}
                onChange={(e) => form.setOutcome(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'outcome')}
            />

            <TextEdit
                label="Tipo/observação da audiência (opcional)"
                value={form.description}
                onChange={(e) => form.setDescription(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'description')}
            />

            {/* Erros de validação (422) sem campo próprio na tela — não deixa sumir. */}
            {residualErrors(errorFields).map((e) => (
                <div key={e.fieldName} className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                    <FiAlertTriangle size={14} />
                    <span>{e.message}</span>
                </div>
            ))}

            <div className="flex items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                >
                    Cancelar
                </button>
                <button
                    type="button"
                    onClick={form.submit}
                    disabled={isSaving || !form.canSubmit}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                    {isSaving ? 'Salvando...' : 'Registrar audiência'}
                </button>
            </div>
        </div>
    );
};

export default LegalCaseHearingForm;
